import { AppBar, Toolbar, Button, Box } from '@mui/material'
import HelpOutlineIcon from '@mui/icons-material/HelpOutline'
import ModeCommentOutlinedIcon from '@mui/icons-material/ModeCommentOutlined'
import { NavLink, useLocation } from 'react-router-dom'

import { Authenticated } from '../utils/auth/auth'
import { LogoutButton } from './AuthButtons'
import { SelectOrg } from './SelectOrg'
import { getCurrentUser } from '@/utils/auth/UserProvider'
import usePermissions from '@/utils/hooks/users'
import { useOnboardingStatus } from '@/pages/onboarding/hooks/useOnboardingStatus'
import { usePausedOrg } from '@/utils/hooks/usePausedOrg'
import NewPill from './NewPill'

interface NavItem {
  id: string
  label: string
  to: string
  isActive: (pathname: string) => boolean
  isNew?: boolean
}

export default function DesktopNav() {
  const { permissions, isLoading: permissionsLoading } = usePermissions()
  const location = useLocation()
  const { isLoading, user, token, orgs: userOrgs } = getCurrentUser()
  const { isOnboarding } = useOnboardingStatus()
  const { isAnnualSubscriber } = usePausedOrg()

  if (isLoading || permissionsLoading) {
    return null
  }

  const accountPath = permissions?.role === 'admin' ? '/account' : '/my-account'

  const organisationNav: NavItem = {
    id: 'organisation',
    label: 'Manage Organisation',
    to: '/profile/details',
    isActive: (pathname: string) =>
      pathname.startsWith('/profile') ||
      pathname.startsWith('/users') ||
      pathname.startsWith('/account') ||
      pathname.startsWith('/my-account') ||
      pathname === accountPath,
  }

  const primaryNav: NavItem[] = isOnboarding
    ? [
        {
          id: 'onboarding',
          label: 'Resume Onboarding',
          to: '/onboarding',
          isActive: (pathname: string) => pathname.startsWith('/onboarding'),
        },
      ]
    : [
        {
          id: 'find',
          label: 'Find Funding',
          to: '/funding-requests',
          isActive: (pathname: string) =>
            pathname.startsWith('/funding-requests') || pathname.startsWith('/match'),
        },
        ...(isAnnualSubscriber
          ? [
              {
                id: 'search',
                label: 'Browse Database',
                to: '/search',
                isActive: (pathname: string) => pathname.startsWith('/search'),
                isNew: true,
              },
            ]
          : []),
        {
          id: 'applications',
          label: 'My Applications',
          to: '/applications',
          isActive: (pathname: string) => pathname.startsWith('/applications'),
        },
      ]

  const isNavActive = (item: NavItem) =>
    item.isActive(location.pathname)

  return (
    <Authenticated>
      <AppBar
        position="static"
        sx={{
          backgroundColor: 'white',
          boxShadow: 'none',
          my: 3,
          mx: 0,
        }}
      >
        <Toolbar
          sx={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: 2,
            px: { xs: 2, md: 4 },
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'flex-start',
              flexGrow: 1,
            }}
          >
            <NavLink to="/" style={{ textDecoration: 'none' }}>
              <img
                src={`/Fundsorter-h-black800px.png`}
                alt="Fundsorter Logo"
                style={{
                  width: '250px',
                  height: 'auto',
                  marginLeft: '20px',
                  cursor: 'pointer',
                }}
              />
            </NavLink>
            <SelectOrg token={token} user={user} userOrgs={userOrgs} />
          </Box>

          <Box
            component="nav"
            aria-label="Main navigation"
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 2,
              mt: 2,
              ml: { md: 3, lg: 4 },
              flexGrow: 1,
              minWidth: 320,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
              {primaryNav.map((item) => (
                <Button
                  key={item.id}
                  component={NavLink}
                  to={item.to}
                  disableRipple
                  sx={{
                    color: '#212121',
                    textTransform: 'none',
                    fontSize: '18px',
                    borderBottom: isNavActive(item)
                      ? '2px solid #1C3045'
                      : '2px solid transparent',
                    borderRadius: 0,
                    '&:hover': { bgcolor: 'transparent' },
                  }}
                >
                  {/* One inline run, so the pill sits beside its label rather
                      than as a separate flex item spaced off from it. */}
                  <span>
                    {item.label}
                    {item.isNew && (
                      <>
                        {' '}
                        <NewPill />
                      </>
                    )}
                  </span>
                </Button>
              ))}
            </Box>
            <Box sx={{ flexGrow: 1 }} />
            <Button
              component={NavLink}
              to={organisationNav.to}
              disableRipple
              sx={{
                color: '#212121',
                textTransform: 'none',
                fontSize: '18px',
                borderBottom: isNavActive(organisationNav)
                  ? '2px solid #1C3045'
                  : '2px solid transparent',
                borderRadius: 0,
                '&:hover': { bgcolor: 'transparent' },
              }}
            >
              {organisationNav.label}
            </Button>
          </Box>

          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              mt: 1,
            }}
          >
            <Button
              component={NavLink}
              to="/feedback"
              disableRipple
              startIcon={<ModeCommentOutlinedIcon />}
              sx={{
                color: '#212121',
                textTransform: 'none',
                fontSize: '16px',
                '&:hover': { bgcolor: 'transparent' },
              }}
            >
              Feedback
            </Button>
            <Button
              component={NavLink}
              to="/help"
              disableRipple
              startIcon={<HelpOutlineIcon />}
              sx={{
                color: '#212121',
                textTransform: 'none',
                fontSize: '16px',
                '&:hover': { bgcolor: 'transparent' },
              }}
            >
              Help
            </Button>
            <LogoutButton />
          </Box>
        </Toolbar>
      </AppBar>
    </Authenticated>
  )
}
